<template>
  <div>
    <ApplicationCalculator
      :errors="$v"
      :contact-number="contactNumber"
      :finance-loading="financeLoading"
      :disable="isExistingQuoteOpen || financeLoading"
      continue-label="Next"
      :novated="quickQuote.novated"
      :summary-calculations="quickQuote.calculation"
      :take-home="quickQuote.takeHome"
      :budget-comparison-view="budgetComparisonView"
      :budget-lease-type-view="budgetLeaseTypeView"
      :calculation-frequency="calculationFrequency"
      :kmsPerYear="customerOptions.kmsPerYear"
      :show-calculation="showFinanceCalculation"
      :annual-salary="customerOptions.annualSalary"
      :car-value="customerOptions.carValue"
      :body-type="customerOptions.bodyType"
      :lease-term="customerOptions.leaseTerm / 12"
      :vehicle-condition="customerOptions.vehicleCondition"
      :vehicle-condition-list="getNewUsedList"
      :body-type-options="Object.values($Constants.VEHICLE_TYPES)"
      :application-saving="applicationSaving"
      :calculator-assumptions-href="calculatorAssumptionsHref"
      :min-kms-per-year="$Constants.KMS_PER_YEAR_MIN[leaseTermAsYear]"
      :max-kms-per-year="$Constants.KMS_PER_YEAR_MAX[leaseTermAsYear]"
      :max-kms-per-year-labels="$Constants.KMS_PER_YEAR_LABELS[leaseTermAsYear]"
      :max-kms-per-year-step="$Constants.KMS_PER_YEAR_STEP[leaseTermAsYear]"
      @update:budget-comparison="setBudgetComparisonView"
      @update:budget-lease-type="setBudgetLeaseTypeView"
      @update:frequency="calculationFrequency = $event"
      @calculation:show="startCalculation"
      @update:annual-salary="updateAnnualSalary"
      @update:car-value="updateCarValue"
      @update:vehicle-condition="updateVehicleCondition"
      @update:body-type="updateBodyType"
      @update:lease-term="updateLeaseTerm"
      @update:kmsPerYear="updateKmsPerYear"
      @continue="goToBudget"
    />
  </div>
</template>

<script>
import scrollToTop from '@/utils/scroll-to-top';
import { getEnv, getTheme } from 'ecx.saigon.ui.common';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import validations from './validations';

export default {
  name: 'CalculatorWidget',
  metaInfo: {
    title: 'Calculator |',
  },
  data() {
    return {
      calculationFrequency:
        this.$Constants.CALCULATION_FREQUENCIES.monthly.value,
      calculatorAssumptionsHref: undefined,
      updateApplicationPaths:
        this.$Constants.APPLICATION_UPDATE_PATHS.calculator,
    };
  },
  computed: {
    ...mapState({
      financeLoading: (state) => state.application.financeLoading,
      quickQuote: (state) => state.application.quickQuote || {},
      quote: (state) => state.quote.quote || {},
      lead: (state) => state.lead.data || {},
      customerOptions: (state) =>
        state.application.application.metaData.customerOptions,
      applicationSaving: (state) => state.application.applicationSaving,
      budgetComparisonView: (state) => state.ui.budgetComparisonView,
      budgetLeaseTypeView: (state) => state.ui.budgetLeaseTypeView,
      user: (state) => state.user.user,
      showFinanceCalculation: (state) => state.ui.showFinanceCalculation,
      leaseId: (state) => state.application.leaseId,
    }),
    ...mapGetters('carSearch', ['getNewUsedList']),
    ...mapGetters('application', ['isApplicationStarted']),
    ...mapGetters('quote', ['isExistingQuoteOpen']),
    leaseTermAsYear() {
      return this.customerOptions.leaseTerm / 12 || 1;
    },
    contactNumber() {
      return getTheme(this.user.entity).support.phone;
    },
  },
  validations,
  methods: {
    ...mapActions('application', [
      'updateApplication',
      'getApplication',
      'getBudgetData',
    ]),
    ...mapMutations('quote', ['setQuote']),
    ...mapMutations('application', ['updateCustomerOption', 'setLeaseId']),
    ...mapMutations('ui', [
      'setBudgetComparisonView',
      'setBudgetLeaseTypeView',
    ]),
    ...mapMutations('carSearch', ['setNewUsedSelected']),
    ...mapMutations('ui', ['setShowFinanceCalculation']),
    async goToBudget() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (this.isApplicationStarted && !this.isExistingQuoteOpen) {
        await this.updateApplication({
          updateApplicationPaths: this.updateApplicationPaths,
        });
      }

      await this.$router.push({
        name: this.$RouteNames.budget,
        params: { ...this.$router.currentRoute.params },
        ...(this.leaseId && { query: { leaseId: this.leaseId } }),
      });
    },
    updateBodyType(value) {
      this.setQuote({
        ...this.quote,
        bodyType: value,
      });
      this.updateCustomerOption({
        field: 'bodyType',
        value,
      });
      this.setShowFinanceCalculation(false);
    },
    updateLeaseTerm(value) {
      this.setQuote({
        ...this.quote,
        leaseTerm: value * 12,
      });
      this.updateCustomerOption({ field: 'leaseTerm', value: value * 12 });

      // Ensure the kmsPerYear value is less than the maximum allowed
      const maxKms = this.$Constants.KMS_PER_YEAR_MAX[this.leaseTermAsYear];
      if (this.customerOptions.kmsPerYear > maxKms) {
        this.updateKmsPerYear(maxKms);
      }

      this.setShowFinanceCalculation(false);
    },
    updateAnnualSalary(value) {
      this.updateCustomerOption({ field: 'annualSalary', value });
      this.setShowFinanceCalculation(false);
    },
    updateCarValue(value) {
      this.updateCustomerOption({ field: 'carValue', value });
      this.setShowFinanceCalculation(false);
    },
    updateVehicleCondition(value) {
      this.updateCustomerOption({ field: 'vehicleCondition', value });
      this.setNewUsedSelected(value);
      this.setShowFinanceCalculation(false);
    },
    updateKmsPerYear(value) {
      this.setQuote({
        ...this.quote,
        kmsPerYear: value,
      });
      this.updateCustomerOption({ field: 'kmsPerYear', value });
      this.setShowFinanceCalculation(false);
    },
    startCalculation() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.setShowFinanceCalculation(true);
      this.getBudgetData();
    },
  },
  async mounted() {
    scrollToTop(this.$el);
    const env = await getEnv();
    this.calculatorAssumptionsHref = env.VUE_APP_NOVATED_CALCULATOR_ASSUMPTIONS;

    if (this.$route.query.leaseId) {
      this.setLeaseId(this.$route.query.leaseId);
    }

    // Case1: landing to calculator for 2nd quote (calc only if no calculation done and no change to calculator)
    // Case2: landing to calculator from Car selection ()
    // Case3: lading to calculator from quick-quote page2 (Running Cost Savings)
    // Case4: page reload
    if (
      this.isApplicationStarted &&
      !this.quickQuote?.novated?.totalTaxSaving &&
      !this.quote?.quoteId
    ) {
      this.startCalculation();
    }
  },
  components: {
    ApplicationCalculator: () =>
      import('../../components/page/application-calculator'),
  },
};
</script>
